import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Clock, Fuel, Radio, CheckCircle } from 'lucide-react';
import { Counter } from '@/hooks/useCountUp';
import { TextReveal } from '@/hooks/useAnimations';

const heroSlides = [
  {
    image: '/images/hero-slide-1.webp',
    subtitle: 'Güvenilir Çözüm Ortağı',
    title: 'Koşullar değişir.\nStandartlarımız değişmez.',
    description: 'Akaryakıt taşımacılığında güvenli, kontrollü ve zamanında operasyon.',
  },
  {
    image: '/images/hero-slide-2.webp',
    subtitle: 'Disiplin ve Güvenlik',
    title: 'Güvenlikte\ntaviz yok.',
    description: 'ADR sertifikalı operasyonlar, uluslararası standartlarda süreç yönetimi.',
  },
  {
    image: '/images/hero-slide-3.webp',
    subtitle: 'Kontrol ve İzlenebilirlik',
    title: 'Her teslimat kontrol altında,\nher yük güvence altında.',
    description: 'Anlık GPS takibi, dijital operasyon yönetimi, şeffaf süreçler ve tüm taşımalarda kapsamlı kasko & sigorta koruması.',
  },
  {
    image: '/images/hero-slide-4.webp',
    subtitle: 'Süreçte Güven',
    title: 'Detaylarda disiplin,\nsüreçte güven.',
    description: 'İhtiyaca özel planlama, zamanında ve güvenli teslimat.',
  },
];

const ease = [0.22, 1, 0.36, 1];

const HeroSlider = () => {
  const [current, setCurrent] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const next = useCallback(() => setCurrent((p) => (p + 1) % heroSlides.length), []);

  useEffect(() => { const t = setInterval(next, 6000); return () => clearInterval(t); }, [next]);
  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <section className="hero-slider" data-testid="hero-section">
      {heroSlides.map((slide, i) => (
        <AnimatePresence key={i}>
          {current === i && (
            <motion.div className="hero-slide" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.2, ease: 'easeInOut' }}>
              <img src={slide.image} alt="" className="hero-slide-image" loading={i === 0 ? 'eager' : 'lazy'} style={{ transform: `translateY(${scrollY * 0.12}px)` }} />
              <div className="hero-overlay" />
            </motion.div>
          )}
        </AnimatePresence>
      ))}

      <div className="hero-content-wrapper">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <AnimatePresence mode="wait">
            <motion.div key={current} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="max-w-2xl">
              <motion.span initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: 0.2 }}
                className="inline-block text-[#FF6B2C] text-xs font-semibold tracking-[0.2em] uppercase mb-6"
              >
                {heroSlides[current].subtitle}
              </motion.span>

              <div className="overflow-hidden mb-7">
                <motion.h1 initial={{ y: '100%' }} animate={{ y: 0 }} transition={{ duration: 0.6, delay: 0.3, ease }}
                  className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-[1.05] tracking-tight whitespace-pre-line"
                >
                  {heroSlides[current].title}
                </motion.h1>
              </div>

              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.5 }}
                className="text-base sm:text-lg text-white/70 leading-relaxed mb-8 sm:mb-10 max-w-md"
              >
                {heroSlides[current].description}
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.6 }} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to="/iletisim">
                  <Button className="w-full sm:w-auto bg-[#FF6B2C] hover:bg-[#E55A1F] text-white rounded-full font-semibold px-7 sm:px-9 py-5 sm:py-6 text-sm sm:text-base shadow-lg shadow-orange-600/20 group" data-testid="hero-cta-quote">
                    Operasyonunuzu planlayalım <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                </Link>
                <Link to="/hizmetler">
                  <Button variant="outline" className="w-full sm:w-auto border-white/25 text-white hover:bg-white/8 rounded-full font-semibold px-7 sm:px-9 py-5 sm:py-6 text-sm sm:text-base" data-testid="hero-cta-fleet">
                    Süreçlerimizi inceleyin
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="hero-pagination">
        {heroSlides.map((_, i) => (
          <button key={i} className={`hero-dot ${current === i ? 'active' : ''}`} onClick={() => setCurrent(i)} data-testid={`hero-dot-${i}`} aria-label={`Slide ${i + 1}`} />
        ))}
      </div>
    </section>
  );
};

/* ── Trust Bar ── */
const TrustBar = () => {
  const stats = [
    { value: 27, suffix: '+', label: 'Yıllık Deneyim' },
    { value: 100, suffix: '%', label: 'Güvenlik Odaklı' },
    { value: 7, suffix: '/24', label: 'Kesintisiz Hizmet' },
    { value: 99, suffix: '%', label: 'Zamanında Teslimat' },
  ];

  return (
    <section className="trust-bar" data-testid="trust-metrics-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-6">
          {stats.map((stat, i) => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, ease }} className="text-center">
              <div className="trust-number"><Counter end={stat.value} duration={2500} />{stat.suffix}</div>
              <div className="trust-label">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── Services — Editorial asymmetric layout ── */
const ServicesSection = () => {
  const services = [
    { icon: Fuel, title: 'Akaryakıt Taşımacılığı', desc: 'Kontrollü yükleme, güvenli taşıma, zamanında teslimat.', href: '/hizmetler/akaryakit' },
    { icon: Shield, title: 'ADR Tehlikeli Madde', desc: 'Sertifikalı ekipman, eğitimli kadro, denetimli süreç.', href: '/hizmetler/adr' },
    { icon: Radio, title: 'Dijital Operasyon Yönetimi', desc: 'Anlık izleme, şeffaf raporlama, tam kontrol.', href: '/hizmetler/filo-takip' },
  ];

  return (
    <section className="section-white section-padding" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <TextReveal><div className="section-label"><span>Hizmetlerimiz</span></div></TextReveal>
            <TextReveal delay={0.1}><h2 className="heading-lg text-[#0B3A82] mb-6">Kontrollü operasyon alanları.</h2></TextReveal>
            <TextReveal delay={0.15}>
              <p className="text-body-lg mb-10">Her hizmet alanımızda aynı standart: güvenlik, kontrol, zamanında teslimat.</p>
            </TextReveal>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
              <Link to="/hizmetler">
                <Button variant="outline" className="border-[#0B3A82] text-[#0B3A82] hover:bg-[#0B3A82] hover:text-white rounded-full font-semibold px-7 transition-all duration-300" data-testid="services-view-all">
                  Tüm Hizmetler <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            {services.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, ease }}>
                <Link to={s.href} className="group flex items-start gap-6 p-7 card-refined rounded-2xl" data-testid={`service-card-${i}`}>
                  <div className="icon-box flex-shrink-0 group-hover:bg-[#0B3A82] group-hover:text-white transition-all duration-300">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-[#1A1A2E] mb-2 group-hover:text-[#0B3A82] transition-colors">{s.title}</h3>
                    <p className="text-sm text-[#64748B] leading-relaxed">{s.desc}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#FF6B2C] group-hover:translate-x-1 transition-all duration-300 flex-shrink-0 mt-1" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Why Us — 4 strong messages, not 6 ── */
const WhyUsSection = () => {
  const reasons = [
    { title: 'Disiplinli Süreç', desc: 'Her operasyon aynı standartla yürütülür. Detay atlanmaz, adım atlanmaz.' },
    { title: 'Denetim Altında Kontrol', desc: 'ISO 9001, ADR ve tüm yasal gereklilikler. Uyum değil, standart.' },
    { title: 'Şeffaf İzlenebilirlik', desc: 'Gerçek zamanlı GPS takip ve dijital raporlama. Her an kontrol sizde.' },
  ];

  return (
    <section className="section-gray section-padding" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div>
            <TextReveal><div className="section-label"><span>Neden Biz</span></div></TextReveal>
            <TextReveal delay={0.1}><h2 className="heading-lg text-[#0B3A82] mb-6">Fark, detaylarda gizlidir.</h2></TextReveal>
            <TextReveal delay={0.15}>
              <p className="text-body-lg">27 yıldır aynı disiplinle çalışıyoruz. Sonuç: güven.</p>
            </TextReveal>
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            {reasons.map((r, i) => (
              <motion.div key={r.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, ease }}
                className="p-6 card-refined rounded-2xl"
              >
                <div className="w-8 h-8 rounded-lg bg-[#0B3A82]/6 flex items-center justify-center text-[#0B3A82] mb-4">
                  <span className="text-sm font-extrabold">0{i + 1}</span>
                </div>
                <h3 className="text-sm font-bold text-[#1A1A2E] mb-2">{r.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{r.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Fleet & Tech — asymmetric, editorial ── */
const FleetTechSection = () => (
  <section className="section-white section-padding" data-testid="fleet-tech-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <div>
          <TextReveal><div className="section-label"><span>Filo & Teknoloji</span></div></TextReveal>
          <TextReveal delay={0.1}><h2 className="heading-lg text-[#0B3A82] mb-6">Kontrol altında operasyon.</h2></TextReveal>
          <TextReveal delay={0.15}>
            <p className="text-body-lg mb-10">Takip sistemleri ve dijital operasyon yönetimi ile her teslimat denetim altında.</p>
          </TextReveal>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.25 }} className="flex flex-wrap gap-3 mb-10">
            {[{ icon: Shield, t: 'ADR Sertifikalı' }, { icon: Radio, t: 'GPS Takip' }, { icon: Clock, t: '7/24 Operasyon' }].map((p, i) => (
              <div key={i} className="feature-pill"><p.icon className="w-4 h-4" /><span>{p.t}</span></div>
            ))}
          </motion.div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.35 }}>
            <Link to="/filo-teknoloji">
              <Button className="bg-[#FF6B2C] hover:bg-[#E55A1F] text-white rounded-full font-semibold px-8 py-3 group shadow-lg shadow-orange-500/15" data-testid="fleet-cta-button">
                Süreçlerimizi inceleyin <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <div className="rounded-2xl overflow-hidden"><img src="/images/tanker-istasyon.webp" alt="Profesyonel Taşımacılık" className="w-full aspect-[4/3] sm:aspect-auto object-cover" loading="lazy" /></div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ── Safety — controlled, not generic ── */
const SafetySection = () => {
  const points = [
    'Düzenli denetim ve bakım programı',
    'ADR sertifikalı operasyon süreci',
    'ISO 9001 kalite yönetim sistemi',
  ];

  return (
    <section className="section-gray section-padding" data-testid="safety-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="order-2 lg:order-1">
            <div className="rounded-2xl overflow-hidden"><img src="/images/yukleme-operasyonu.webp" alt="Güvenli Taşımacılık" className="w-full aspect-[4/3] sm:aspect-auto object-cover" loading="lazy" /></div>
          </motion.div>

          <div className="order-1 lg:order-2">
            <TextReveal><div className="section-label"><span>Güvenlik & Kalite</span></div></TextReveal>
            <TextReveal delay={0.1}><h2 className="heading-lg text-[#0B3A82] mb-6">Güvenlik. Taviz yok.</h2></TextReveal>
            <TextReveal delay={0.15}>
              <p className="text-body-lg mb-10">Operatör denetimi, süreç kontrolü ve düzenli bakım. Güvenliğimiz sistemle değil, disiplinle kurulur.</p>
            </TextReveal>
            <div className="check-list mb-10">
              {points.map((p, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.25 + i * 0.06, ease }} className="check-item">
                  <div className="check-icon"><CheckCircle /></div>
                  <span className="text-[#1A1A2E] font-medium text-[0.9375rem]">{p}</span>
                </motion.div>
              ))}
            </div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 }}>
              <Link to="/guvenlik-kalite">
                <Button variant="outline" className="border-[#0B3A82] text-[#0B3A82] hover:bg-[#0B3A82] hover:text-white rounded-full font-semibold px-7 transition-all duration-300" data-testid="safety-cta-button">
                  Denetim sürecimiz <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── References — Curated grid by sector, not marquee ── */
const referenceLogos = [
  { name: 'Shell', logo: '/images/logos/shell.webp', className: '' },
  { name: 'Petrol Ofisi', logo: '/images/logos/petrol-ofisi.webp', className: '' },
  { name: 'Total', logo: '/images/logos/total.webp', className: '' },
  { name: 'Opet', logo: '/images/logos/opet.webp', className: '' },
  { name: 'Lukoil', logo: '/images/logos/lukoil.webp', className: '' },
  { name: 'Aytemiz', logo: '/images/logos/aytemiz.webp', className: '' },
  { name: 'Alpet', logo: '/images/logos/alpet.webp', className: '' },
];

const ReferencesSection = () => (
  <section className="section-white section-padding-sm" data-testid="references-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-12">
      <div className="text-center">
        <TextReveal><div className="section-label justify-center"><span>Referanslarımız</span></div></TextReveal>
        <TextReveal delay={0.1}><h2 className="heading-lg text-[#0B3A82] mb-4">Uzun vadeli güven.</h2></TextReveal>
        <TextReveal delay={0.15}><p className="text-body-lg">Sektörün lider firmalarıyla sürdürülebilir iş birlikleri.</p></TextReveal>
      </div>
    </div>
    <div className="logo-marquee-wrapper" data-testid="references-marquee">
      <div className="logo-marquee">
        {[...referenceLogos, ...referenceLogos].map((ref, i) => (
          <div key={`${ref.name}-${i}`} className="logo-marquee-item">
            <img src={ref.logo} alt={ref.name} className={`h-14 sm:h-16 lg:h-20 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity duration-300 ${ref.className}`} loading="lazy" />
          </div>
        ))}
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-12 text-center">
      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.25 }}>
        <Link to="/referanslar">
          <Button variant="outline" className="border-[#E2E8F0] text-[#1A1A2E] hover:border-[#0B3A82] hover:text-[#0B3A82] rounded-full font-semibold px-7" data-testid="references-cta-button">
            Tüm Referanslar <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </motion.div>
    </div>
  </section>
);

/* ── CTA ── */
const CTASection = () => (
  <section className="cta-section section-padding-sm" data-testid="cta-section">
    <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center relative z-10">
      <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
        <h2 className="heading-lg text-white mb-6">Operasyonunuzu birlikte planlayalım.</h2>
        <p className="text-white/60 mb-12 text-base leading-relaxed">
          Size özel çözümü konuşmak için ekibimiz hazır.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <Link to="/iletisim">
            <Button className="w-full sm:w-auto bg-[#FF6B2C] hover:bg-[#E55A1F] text-white rounded-full font-semibold px-8 sm:px-10 py-5 sm:py-6 text-sm sm:text-base shadow-lg" data-testid="cta-quote-button">
              Ekibimizle görüşün <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <a href="tel:+905305450170">
            <Button variant="outline" className="w-full sm:w-auto border-white/20 text-white hover:bg-white/8 rounded-full font-semibold px-8 sm:px-10 py-5 sm:py-6 text-sm sm:text-base" data-testid="cta-call-button">
              0530 545 01 70
            </Button>
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ── Page ── */
const HomePage = () => (
  <div data-testid="home-page" className="bg-white">
    <HeroSlider />
    <TrustBar />
    <ServicesSection />
    <WhyUsSection />
    <FleetTechSection />
    <SafetySection />
    <ReferencesSection />
    <CTASection />
  </div>
);

export default HomePage;
